import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Clock, MapPin } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { getTimezoneDisplayName, areTimezonesDifferent } from '@/utils/timezone-detection';

interface TimezoneWarningModalProps {
  open: boolean;
  onConfirm: () => void;
  clientTimezone: string | null;
  practitionerTimezone: string;
  appointmentTime: Date;
  practitionerName?: string;
}

const TimezoneWarningModal: React.FC<TimezoneWarningModalProps> = ({
  open,
  onConfirm,
  clientTimezone,
  practitionerTimezone,
  appointmentTime,
  practitionerName = 'the practitioner'
}) => {
  const formatTimeInTimezone = (date: Date, timezone: string) => {
    try {
      return formatInTimeZone(date, timezone, 'PPP p (zzz)');
    } catch (error) {
      console.error('Error formatting time in timezone:', error);
      return date.toLocaleString();
    }
  };

  const getDisplayName = (timezone: string | null) => {
    if (!timezone) return 'Not set';
    return getTimezoneDisplayName(timezone);
  };

  const isTimezoneNotSet = !clientTimezone;
  const isDifferentTimezone = clientTimezone && clientTimezone !== practitionerTimezone;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Timezone Notice
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              {isTimezoneNotSet && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 font-medium mb-2">
                    Please set your timezone
                  </p>
                  <p className="text-amber-700 text-sm">
                    You haven't set your timezone yet. This appointment will be shown in {getTimezoneDisplayName(practitionerTimezone)}.
                  </p>
                </div>
              )}
              
              {isDifferentTimezone && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800 font-medium mb-2">
                    Your timezone is different from {practitionerName}'s timezone
                  </p>
                  
                  <div className="space-y-3 mt-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-blue-700">Your time:</p>
                        <p className="text-sm text-blue-600">
                          {formatTimeInTimezone(appointmentTime, clientTimezone)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-blue-700">{practitionerName}'s time:</p>
                        <p className="text-sm text-blue-600">
                          {formatTimeInTimezone(appointmentTime, practitionerTimezone)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-700 text-sm">
                  Please verify the appointment time is correct for your timezone before proceeding.
                  {isTimezoneNotSet && ' You can update your timezone in your profile settings.'}
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction 
            onClick={onConfirm}
            className="bg-primary hover:bg-primary/90"
          >
            I understand
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default TimezoneWarningModal;